import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../../config/api'

export const SMR_P = { pri: '#0a5169', priD: '#07384a', priM: '#e8f2f6', acc: '#fde300' }

export const OFFENCE_OPTIONS = [
  { value: 'money_laundering', label: 'Money laundering' },
  { value: 'terrorism_financing', label: 'Terrorism financing' },
  { value: 'proceeds_of_crime', label: 'Proceeds of crime' },
  { value: 'tax_evasion', label: 'Tax evasion' },
  { value: 'fraud', label: 'Fraud' },
  { value: 'identity_fraud', label: 'Identity fraud' },
  { value: 'other_commonwealth', label: 'Other Commonwealth' },
  { value: 'other_state', label: 'Other State' },
  { value: 'proliferation_financing', label: 'Proliferation financing' },
  { value: 'sanctions_breach', label: 'Sanctions breach' },
]

export const INDICATOR_OPTIONS = [
  { value: 'structuring', label: 'Structuring' },
  { value: 'cuckoo_smurfing', label: 'Cuckoo smurfing' },
  { value: 'third_party_deposits', label: 'Third party deposits' },
  { value: 'rapid_movement_funds', label: 'Rapid movement of funds' },
  { value: 'inconsistent_profile', label: 'Inconsistent with profile' },
  { value: 'unusual_transaction_patterns', label: 'Unusual transaction patterns' },
  { value: 'false_identity', label: 'False identity' },
  { value: 'reluctance_to_provide_id', label: 'Reluctance to provide ID' },
  { value: 'minimal_gameplay', label: 'Minimal gameplay' },
  { value: 'other', label: 'Other' },
]

export const DESIGNATED_SERVICE_OPTIONS = [
  { value: 'gambling_service', label: 'Gambling service' },
  { value: 'account_service', label: 'Account service' },
  { value: 'currency_exchange', label: 'Currency exchange' },
]

export function setDeep(obj, path, value) {
  if (!obj || typeof obj !== 'object') return obj
  const keys = path.split('.')
  const next = JSON.parse(JSON.stringify(obj))
  let cur = next
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {}
    else cur[k] = { ...cur[k] }
    cur = cur[k]
  }
  cur[keys[keys.length - 1]] = value
  return next
}

export function preFieldHasValue(value) {
  if (value == null) return false
  const s = String(value).trim()
  return s !== '' && s !== '—' && s !== '-'
}

export function smrReportDeadlineOverdue(meta) {
  if (!meta || typeof meta !== 'object') return false
  if (meta.reportDeadlineIso) {
    const t = Date.parse(meta.reportDeadlineIso)
    return Number.isFinite(t) && Date.now() > t
  }
  const s = meta.reportDeadline
  if (!s || typeof s !== 'string') return false
  const m = s.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})/)
  if (!m) return false
  const [, dd, mm, yyyy, HH, MM, SS] = m
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(HH), Number(MM), Number(SS))
  return Number.isFinite(d.getTime()) && Date.now() > d.getTime()
}

export function sanitizeStepNoteForSmr(note) {
  if (!note || typeof note !== 'string') return null
  if (/migration\s*104/i.test(note) || /SMR pipeline backfill/i.test(note)) return null
  return note
}

export function computeChecklist(d, previousSmrCount) {
  if (!d) return { items: [], passed: 0, total: 14 }
  const g = d.grounds || {}
  const ri = d.reportInfo || {}
  const sp = d.suspiciousPerson || {}
  const tx = Array.isArray(d.transactions) ? d.transactions : []
  const co = d.completingOfficer || {}
  const sc = d.suspicionContact || {}
  const nar = (g.B1_narrative || '').trim()
  const rules2025 = d.form_format === 'rules_2025'
  const officerOk =
    !!(co.A13_name && co.A14_position && co.A15_phone && (rules2025 ? co.A16_email : true))
  const contactOk =
    !!(sc.A17_name && sc.A18_position && sc.A19_phone && (rules2025 ? sc.A20_email : true))
  const refs = ri.A11_relatedSmrRefs
  const hasA11 = Array.isArray(refs) && refs.some((x) => String(x).trim())
  let smrCrossRefOk = true
  if (previousSmrCount != null && previousSmrCount > 0) {
    smrCrossRefOk = hasA11
  }
  const items = [
    { id: 1, label: 'Timeliness', ok: !!(ri.A8_suspicionFormedDate && ri.A7_reportDate) },
    { id: 2, label: 'Six essential elements (narrative length)', ok: nar.length > 200 },
    { id: 3, label: 'Plain English', ok: true },
    { id: 4, label: 'Financial amounts in transactions', ok: tx.some((t) => t.F4_amount != null && t.F4_amount !== '') },
    { id: 5, label: 'Red flags (indicators)', ok: Array.isArray(g.B4_indicators) && g.B4_indicators.length > 0 },
    { id: 6, label: 'Consistent with profile', ok: /profile|inconsistent/i.test(nar) },
    { id: 7, label: 'Identity details', ok: !!(sp.C2_familyName && sp.C3_givenNames && (sp.C5_dob || sp.C6_approxAge)) },
    { id: 8, label: 'Previous SMR cross-referenced (A11)', ok: smrCrossRefOk },
    { id: 9, label: 'Offence type', ok: Array.isArray(g.B2_offenceType) && g.B2_offenceType.length > 0 },
    { id: 10, label: 'Transactions attached', ok: tx.length > 0 },
    { id: 11, label: 'No tipping off', ok: true },
    { id: 12, label: 'Completing officer', ok: officerOk },
    { id: 13, label: 'Suspicion contact', ok: contactOk },
    { id: 14, label: 'ECDD not delaying SMR', ok: true },
  ]
  const passed = items.filter((i) => i.ok).length
  return { items, passed, total: items.length }
}

export const CRITICAL_CHECK_IDS = new Set([1, 7, 8, 9, 10, 12, 13])

export function getCriticalFailures(checklist) {
  if (!checklist?.items) return []
  return checklist.items.filter((i) => CRITICAL_CHECK_IDS.has(i.id) && !i.ok).map((i) => i.label)
}

/** Map checklist labels to SMR Part tab for “fix here” navigation. */
export const SMR_CRITICAL_PART_HINTS = {
  Timeliness: 'A',
  'Identity details': 'C',
  'Previous SMR cross-referenced (A11)': 'B',
  'Offence type': 'G',
  'Transactions attached': 'F',
  'Completing officer': 'B',
  'Suspicion contact': 'B',
}

export function getSubmitBlockers(checklist, dateRangeValidation) {
  const critical = getCriticalFailures(checklist).map((label) => ({
    kind: 'checklist',
    label,
    part: SMR_CRITICAL_PART_HINTS[label] || null,
  }))
  if (dateRangeValidation && dateRangeValidation.valid === false) {
    critical.push({
      kind: 'dates',
      label: 'Transaction / evidence date range exceeds 3 days',
      part: 'F',
      detail: dateRangeValidation.message,
    })
  }
  return critical
}

/** Align with backend: legacy / empty case_forms may omit `grounds`+`reportInfo` (SmrFormPanelV2 shows blanks). */
export function smrDraftMissingV2Shape(d) {
  if (!d || typeof d !== 'object') return true
  if (!d.reportInfo || typeof d.reportInfo !== 'object') return true
  if (!d.grounds || typeof d.grounds !== 'object') return true
  const nonEmpty = (v) => v != null && String(v).trim() !== ''
  const ri = d.reportInfo
  if (!nonEmpty(ri.A7_reportDate) || !nonEmpty(ri.A8_suspicionFormedDate)) return true
  const g = d.grounds
  const b4 = Array.isArray(g.B4_indicators) ? g.B4_indicators : []
  const hasGrounds = nonEmpty(g.B1_narrative) || b4.length > 0
  return !hasGrounds
}

/**
 * Shared SMR draft load/save/submit logic for SmrFormPanel and SmrFormPanelV2.
 */
function toLocalDatetimeInput(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function useSmrFormPanel({ caseId, memberId, onFiled }) {
  const [draft, setDraft] = useState(null)
  const [source, setSource] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [autoSaving, setAutoSaving] = useState(false)
  const [lastAutoSaved, setLastAutoSaved] = useState(null)
  const [previousSmrCount, setPreviousSmrCount] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submittedForReview, setSubmittedForReview] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [submittingToAustrac, setSubmittingToAustrac] = useState(false)
  const [austracRef, setAustracRef] = useState('')
  const [showAustracModal, setShowAustracModal] = useState(false)
  const [filingMethod, setFilingMethod] = useState('manual')
  const [filedAtLocal, setFiledAtLocal] = useState(() => {
    const d = new Date()
    d.setSeconds(0, 0)
    return toLocalDatetimeInput(d)
  })
  const [filingNotes, setFilingNotes] = useState('')
  const [addToWatchlist, setAddToWatchlist] = useState(false)
  const [onWatchlist, setOnWatchlist] = useState(false)
  const [watchlistLoading, setWatchlistLoading] = useState(false)
  const [confirmationImage, setConfirmationImage] = useState(null)
  const draftRef = useRef(null)
  const staleRegenDoneRef = useRef(false)

  useEffect(() => {
    staleRegenDoneRef.current = false
  }, [caseId])

  const load = useCallback(
    async (regenerate) => {
      if (!caseId) return
      setLoading(true)
      try {
        const q = regenerate ? '?regenerate=true' : ''
        let res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/smr${q}`))
        let json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
        if (!json.success) throw new Error(json.error || 'Failed to load SMR draft')
        if (!regenerate && smrDraftMissingV2Shape(json.data) && !staleRegenDoneRef.current) {
          staleRegenDoneRef.current = true
          res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/smr?regenerate=true`))
          json = await res.json().catch(() => ({}))
          if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
          if (!json.success) throw new Error(json.error || 'Failed to load SMR draft')
        }
        setDraft(json.data)
        setSource(json.source || 'auto')
        setDirty(false)
        setLastAutoSaved(null)
      } catch (e) {
        toast.error(e.message || 'Failed to load SMR draft')
        setDraft(null)
      } finally {
        setLoading(false)
      }
    },
    [caseId]
  )

  useEffect(() => {
    load(false)
  }, [load])

  useEffect(() => {
    if (!caseId) {
      setSubmittedForReview(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(
          getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/SMR/submissions`)
        )
        const json = await res.json().catch(() => ({}))
        if (cancelled) return
        if (res.ok && json.success && Array.isArray(json.data) && json.data.length > 0) {
          setSubmittedForReview(true)
        } else {
          setSubmittedForReview(false)
        }
      } catch {
        if (!cancelled) setSubmittedForReview(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [caseId])

  useEffect(() => {
    draftRef.current = draft
  }, [draft])

  useEffect(() => {
    if (!caseId || !memberId) {
      setPreviousSmrCount(0)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/previous-smrs`))
        const j = await res.json().catch(() => ({}))
        if (cancelled) return
        if (res.ok && j.success && Array.isArray(j.data)) setPreviousSmrCount(j.data.length)
        else setPreviousSmrCount(null)
      } catch {
        if (!cancelled) setPreviousSmrCount(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [caseId, memberId])

  const persistDraft = useCallback(
    async (body, { silent } = {}) => {
      if (!caseId || !body) return false
      const sent = JSON.stringify(body)
      if (!silent) setSaving(true)
      else setAutoSaving(true)
      try {
        const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/smr`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: sent,
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
        setSource('draft')
        if (silent) {
          if (JSON.stringify(draftRef.current) === sent) setDirty(false)
          setLastAutoSaved(Date.now())
        } else {
          setDirty(false)
          toast.success('Draft saved')
        }
        return true
      } catch (e) {
        if (!silent) toast.error(e.message || 'Save failed')
        return false
      } finally {
        if (!silent) setSaving(false)
        else setAutoSaving(false)
      }
    },
    [caseId]
  )

  useEffect(() => {
    if (!dirty || !caseId || loading) return
    const t = setTimeout(() => {
      const body = draftRef.current
      if (body) persistDraft(body, { silent: true })
    }, 3000)
    return () => clearTimeout(t)
  }, [draft, dirty, caseId, loading, persistDraft])

  const updatePath = useCallback((path, value) => {
    setDraft((prev) => {
      if (!prev) return prev
      return setDeep(prev, path, value)
    })
    setDirty(true)
  }, [])

  const toggleB2 = useCallback((value) => {
    setDraft((prev) => {
      if (!prev) return prev
      const arr = Array.isArray(prev.grounds?.B2_offenceType) ? [...prev.grounds.B2_offenceType] : []
      const i = arr.indexOf(value)
      if (i >= 0) arr.splice(i, 1)
      else arr.push(value)
      return setDeep(prev, 'grounds.B2_offenceType', arr)
    })
    setDirty(true)
  }, [])

  const toggleB4 = useCallback((value) => {
    setDraft((prev) => {
      if (!prev) return prev
      const arr = Array.isArray(prev.grounds?.B4_indicators) ? [...prev.grounds.B4_indicators] : []
      const i = arr.indexOf(value)
      if (i >= 0) arr.splice(i, 1)
      else arr.push(value)
      return setDeep(prev, 'grounds.B4_indicators', arr)
    })
    setDirty(true)
  }, [])

  const setFormat = useCallback((fmt) => {
    setDraft((prev) => (prev ? { ...prev, form_format: fmt } : prev))
    setDirty(true)
  }, [])

  const updateTransaction = useCallback((index, key, value) => {
    setDraft((prev) => {
      if (!prev) return prev
      const tx = [...(prev.transactions || [])]
      if (!tx[index]) return prev
      tx[index] = { ...tx[index], [key]: value }
      const F16_totalAmount = tx.reduce((s, t) => s + (Number(t.F4_amount) || 0), 0)
      return {
        ...prev,
        transactions: tx,
        transactionSummary: {
          ...(prev.transactionSummary || {}),
          F15_count: tx.length,
          F16_totalAmount,
        },
      }
    })
    setDirty(true)
  }, [])

  const addTransactionRow = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev
      const tx = [...(prev.transactions || [])]
      tx.push({
        F1_date: '',
        F2_time: '',
        F3_type: 'CASH_IN',
        F4_amount: null,
        F5_currency: 'AUD',
        F8_paymentMethod: 'cash',
        F10_location: '',
        F11_machineId: null,
        F12_staffMember: null,
        N113_designatedServiceType: 'gambling_service',
      })
      const F16_totalAmount = tx.reduce((s, t) => s + (Number(t.F4_amount) || 0), 0)
      return {
        ...prev,
        transactions: tx,
        transactionSummary: {
          ...(prev.transactionSummary || {}),
          F15_count: tx.length,
          F16_totalAmount,
        },
      }
    })
    setDirty(true)
  }, [])

  const addOnlineRow = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev
      const rows = [...(prev.onlineActivity || [])]
      rows.push({
        N118_ip: '',
        N119_date: '',
        N120_time: '',
        N121_imei: '',
        N122_deviceId: '',
        N123_software: '',
        N124_usernames: [],
      })
      return { ...prev, onlineActivity: rows }
    })
    setDirty(true)
  }, [])

  const updateOnlineRow = useCallback((index, patch) => {
    setDraft((prev) => {
      if (!prev) return prev
      const rows = [...(prev.onlineActivity || [])]
      if (!rows[index]) return prev
      rows[index] = { ...rows[index], ...patch }
      return { ...prev, onlineActivity: rows }
    })
    setDirty(true)
  }, [])

  const addAgencyRow = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev
      const ad = prev.agencyDisclosure || { A21_reportedToAgency: false, agencies: [] }
      const agencies = [...(ad.agencies || [])]
      agencies.push({ A22_name: '', A23_contact: '', A24_ref: '', A25_date: '', A26_summary: '' })
      return { ...prev, agencyDisclosure: { ...ad, A21_reportedToAgency: true, agencies } }
    })
    setDirty(true)
  }, [])

  const updateAgencyRow = useCallback((index, patch) => {
    setDraft((prev) => {
      if (!prev) return prev
      const ad = prev.agencyDisclosure || { agencies: [] }
      const agencies = [...(ad.agencies || [])]
      if (!agencies[index]) return prev
      agencies[index] = { ...agencies[index], ...patch }
      return { ...prev, agencyDisclosure: { ...ad, agencies } }
    })
    setDirty(true)
  }, [])

  const removeTransactionRow = useCallback((index) => {
    setDraft((prev) => {
      if (!prev) return prev
      const tx = [...(prev.transactions || [])]
      tx.splice(index, 1)
      const F16_totalAmount = tx.reduce((s, t) => s + (Number(t.F4_amount) || 0), 0)
      return {
        ...prev,
        transactions: tx,
        transactionSummary: {
          ...(prev.transactionSummary || {}),
          F15_count: tx.length,
          F16_totalAmount,
        },
      }
    })
    setDirty(true)
  }, [])

  const downloadEvidenceFile = useCallback(
    async (stepId, evidenceId, fileName) => {
      if (!caseId || evidenceId == null || stepId == null) return
      try {
        const res = await authFetch(
          getLocalApiUrl(
            `/api/cases/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/evidence-files/${encodeURIComponent(evidenceId)}`
          )
        )
        if (!res.ok) throw new Error('Download failed')
        const blob = await res.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName || 'evidence'
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        toast.error(e.message || 'Download failed')
      }
    },
    [caseId]
  )

  const checklist = useMemo(() => computeChecklist(draft, previousSmrCount), [draft, previousSmrCount])

  const saveDraft = useCallback(() => {
    if (draft) persistDraft(draft, { silent: false })
  }, [draft, persistDraft])

  const submitForReview = useCallback(async () => {
    if (!caseId || !draft) return
    const critical = getCriticalFailures(checklist)
    if (critical.length > 0) {
      toast.error(`Fix required before submit: ${critical.join('; ')}`)
      return
    }
    const dvSubmit = draft.meta?.dateRangeValidation
    if (dvSubmit && !dvSubmit.valid) {
      toast.error('Transaction dates exceed the 3-day window — adjust transactions before submit.')
      return
    }
    setSubmitting(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/smr/submit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      toast.success('SMR submitted for review')
      setDirty(false)
      setSubmittedForReview(true)
    } catch (e) {
      toast.error(e.message || 'Submit failed')
    } finally {
      setSubmitting(false)
    }
  }, [caseId, draft, checklist])

  const handleDownloadPdf = useCallback(
    async (caseNumber) => {
      setDownloadingPdf(true)
      try {
        const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/smr/download`))
        if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`)
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `SMR_${caseNumber || caseId}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        toast.success('PDF downloaded')
      } catch (e) {
        toast.error(e.message || 'PDF download failed')
      } finally {
        setDownloadingPdf(false)
      }
    },
    [caseId]
  )

  const openAustracModal = useCallback(async () => {
    setShowAustracModal(true)
    setOnWatchlist(false)
    setAddToWatchlist(false)
    const mid = memberId && String(memberId).trim() && memberId !== '—' ? String(memberId).trim() : ''
    if (!mid) return
    setWatchlistLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(mid)}/overview`))
      const json = await res.json().catch(() => ({}))
      const count = Number(json?.data?.watchlist?.count || 0)
      const alreadyOn = count > 0
      setOnWatchlist(alreadyOn)
      setAddToWatchlist(!alreadyOn)
    } catch {
      setOnWatchlist(false)
      setAddToWatchlist(true)
    } finally {
      setWatchlistLoading(false)
    }
  }, [memberId])

  const closeAustracModal = useCallback(() => {
    if (submittingToAustrac) return
    setShowAustracModal(false)
    setAustracRef('')
    setFilingNotes('')
    setConfirmationImage(null)
  }, [submittingToAustrac])

  const handleSubmitToAustrac = useCallback(async () => {
    if (!austracRef.trim()) {
      toast.error('Enter the AUSTRAC reference number from the submission confirmation')
      return
    }
    if (!filedAtLocal || !filingMethod) {
      toast.error('Filing method and filed date are required')
      return
    }
    setSubmittingToAustrac(true)
    try {
      const payload = {
        austrac_reference: austracRef.trim(),
        filing_method: filingMethod,
        filed_at: new Date(filedAtLocal).toISOString(),
        notes: filingNotes.trim() || undefined,
        add_to_watchlist: Boolean(addToWatchlist && !onWatchlist),
      }
      if (confirmationImage?.file) {
        const dataUrl = await fileToDataUrl(confirmationImage.file)
        payload.confirmation_image = {
          data: dataUrl,
          mime_type: confirmationImage.file.type || 'image/png',
          filename: confirmationImage.name || 'austrac-confirmation.png',
        }
      }
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/smr/submit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      const wl = json.data?.watchlist
      let msg = 'SMR marked as filed with AUSTRAC'
      if (wl?.added) msg += ' — patron added to watchlist'
      else if (wl?.reason === 'already_on_watchlist') msg += ' — patron already on watchlist'
      toast.success(msg)
      setShowAustracModal(false)
      setAustracRef('')
      setFilingNotes('')
      setConfirmationImage(null)
      onFiled?.()
    } catch (e) {
      toast.error(e.message || 'Failed to record AUSTRAC submission')
    } finally {
      setSubmittingToAustrac(false)
    }
  }, [
    caseId,
    austracRef,
    filingMethod,
    filedAtLocal,
    filingNotes,
    addToWatchlist,
    onWatchlist,
    confirmationImage,
    onFiled,
  ])

  return {
    draft,
    source,
    loading,
    saving,
    autoSaving,
    lastAutoSaved,
    previousSmrCount,
    submitting,
    submittedForReview,
    dirty,
    showPreview,
    setShowPreview,
    downloadingPdf,
    submittingToAustrac,
    austracRef,
    setAustracRef,
    showAustracModal,
    openAustracModal,
    closeAustracModal,
    filingMethod,
    setFilingMethod,
    filedAtLocal,
    setFiledAtLocal,
    filingNotes,
    setFilingNotes,
    addToWatchlist,
    setAddToWatchlist,
    onWatchlist,
    watchlistLoading,
    confirmationImage,
    setConfirmationImage,
    load,
    persistDraft,
    updatePath,
    toggleB2,
    toggleB4,
    setFormat,
    updateTransaction,
    addTransactionRow,
    removeTransactionRow,
    addOnlineRow,
    updateOnlineRow,
    addAgencyRow,
    updateAgencyRow,
    downloadEvidenceFile,
    checklist,
    saveDraft,
    submitForReview,
    handleDownloadPdf,
    handleSubmitToAustrac,
  }
}
